/**
 * calendarEvent.ts
 *
 * Single shared "add to calendar" implementation. Previously this logic
 * was written twice — once in VenueDetailPage.tsx (serves a real .ics via
 * /api/calendar-event on iOS, which opens the native event-preview sheet;
 * Google Calendar template elsewhere) and once, independently, in
 * TonightPage.tsx (a client-side blob-download trick for iOS that isn't
 * the same reliability guarantee, plus a button label that hardcoded
 * "Add to Google Calendar" on every non-iOS device instead of the
 * platform-neutral "Add to Calendar" the venue detail page already used).
 * Two implementations of one feature is how they silently drift apart —
 * this file is the one place either page should ever need to change.
 */

import type { HappyHourSchedule, Venue } from '../types'

const DAY_TO_RRULE: Record<string, string> = {
  Mon: 'MO', Tue: 'TU', Wed: 'WE', Thu: 'TH',
  Fri: 'FR', Sat: 'SA', Sun: 'SU',
}
const JS_DAY_TO_ABBR = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat']

export function isIOSDevice(): boolean {
  return /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1)
}

// First upcoming date (today included) that falls on one of the
// schedule's days — anchors the event to the correct real day instead
// of always "today," which broke for e.g. a Monday-only schedule viewed
// on a Tuesday.
function nextDateForSchedule(days: string[]): Date {
  const d = new Date()
  for (let i = 0; i < 7; i++) {
    if (days.includes(JS_DAY_TO_ABBR[d.getDay()] as any)) return d
    d.setDate(d.getDate() + 1)
  }
  return new Date()
}

export function addToCalendar(
  venue: Pick<Venue, 'name' | 'address' | 'neighborhood'>,
  schedule: HappyHourSchedule,
  openExternal: (url: string) => void
) {
  const base = nextDateForSchedule(schedule.days)
  const [sh, sm] = schedule.start_time.split(':').map(Number)
  const [eh, em] = schedule.end_time.split(':').map(Number)
  const start = new Date(base.getFullYear(), base.getMonth(), base.getDate(), sh, sm || 0)
  const end   = new Date(base.getFullYear(), base.getMonth(), base.getDate(), eh, em || 0)
  // Past-midnight end (10 PM – 1 AM): end lands on the next day
  if (end <= start) end.setDate(end.getDate() + 1)

  const title = `Happy Hour at ${venue.name}`
  const details = schedule.deal_text || 'Happy hour deals'
  const location = venue.address || venue.neighborhood || ''
  const byday = schedule.days.map(d => DAY_TO_RRULE[d]).filter(Boolean).join(',')

  if (isIOSDevice()) {
    // Serve a real .ics — iOS opens the native event preview, which
    // adds to Apple Calendar. Floating local times, no TZ conversion.
    const fmtLocal = (d: Date) =>
      `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}${String(d.getDate()).padStart(2, '0')}` +
      `T${String(d.getHours()).padStart(2, '0')}${String(d.getMinutes()).padStart(2, '0')}00`
    const params = new URLSearchParams({
      title, details, location,
      dtstart: fmtLocal(start),
      dtend:   fmtLocal(end),
    })
    if (byday) params.set('byday', byday)
    openExternal(`${window.location.origin}/api/calendar-event?${params.toString()}`)
  } else {
    // Android / desktop: Google Calendar template (UTC-formatted)
    const fmt = (d: Date) => d.toISOString().replace(/[-:]/g, '').split('.')[0] + 'Z'
    const rrule = byday ? `RRULE:FREQ=WEEKLY;BYDAY=${byday}` : 'RRULE:FREQ=WEEKLY'
    const url = `https://calendar.google.com/calendar/render?action=TEMPLATE&text=${encodeURIComponent(title)}&dates=${fmt(start)}/${fmt(end)}&details=${encodeURIComponent(details)}&location=${encodeURIComponent(location)}&recur=${encodeURIComponent(rrule)}`
    openExternal(url)
  }
}

// Button label is ALWAYS "Add to Calendar" — the underlying action
// differs by platform (real .ics vs Google Calendar template), but the
// user never needs to know that; the button shouldn't advertise which
// calendar provider it's about to use.
export const ADD_TO_CALENDAR_LABEL = '📅 Add to Calendar'
